import { Warranty } from '@wsphere/warranties/domain';

/** Statuses that make a warranty ineligible for new claims. */
export const INELIGIBLE_WARRANTY_STATUSES: ReadonlyArray<Warranty.Status> = [
  Warranty.Status.CANCELLED,
  Warranty.Status.EXPIRED,
  Warranty.Status.DRAFT,
  Warranty.Status.VOID,
];

/**
 * Returns `true` when the warranty cannot have a new claim opened against it.
 * A frozen warranty is always ineligible regardless of its status.
 */
export function isWarrantyIneligible(warranty: Warranty.Model | null): boolean {
  if (!warranty) return false;
  return warranty.frozen || INELIGIBLE_WARRANTY_STATUSES.includes(warranty.status);
}

/** Returns the short title to display in ineligibility toasts / banners. */
export function warrantyIneligibleTitle(warranty: Warranty.Model | null): string {
  if (!warranty) return 'Warranty Not Eligible';
  if (warranty.frozen) return 'Warranty Frozen';
  if (warranty.status === Warranty.Status.CANCELLED) return 'Warranty Cancelled';
  if (warranty.status === Warranty.Status.EXPIRED) return 'Warranty Expired';
  if (warranty.status === Warranty.Status.VOID) return 'Warranty Void';
  return 'Warranty In Draft';
}

/** Returns the descriptive message explaining why the warranty is ineligible. */
export function warrantyIneligibleMessage(warranty: Warranty.Model | null): string {
  if (!warranty) return 'This warranty is not eligible for new claims.';
  if (warranty.frozen) {
    return 'This warranty is frozen because of a payment issue. New claims are unavailable until the payment problem is resolved.';
  }
  if (warranty.status === Warranty.Status.CANCELLED) {
    return 'This warranty has been cancelled, so new claims cannot be created.';
  }
  if (warranty.status === Warranty.Status.EXPIRED) {
    return 'This warranty has expired, so new claims cannot be created.';
  }
  if (warranty.status === Warranty.Status.VOID) {
    return 'This warranty is void, so new claims cannot be created.';
  }
  return 'This warranty is still in draft status, so new claims cannot be created yet.';
}

/** Returns the icon name appropriate for the ineligibility state. */
export function warrantyIneligibleIcon(warranty: Warranty.Model | null): 'snowflake-01' | 'alert-octagon' {
  return warranty?.frozen ? 'snowflake-01' : 'alert-octagon';
}
